import { useCallback, useEffect, useRef, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';

import {
  DonorInfo,
  Run,
  SireInfo,
  useAssignDonorToRunMutation,
  useCreateEmptyRunMutation,
  useDeleteRunMutation,
  useGenerateRunsMutation,
  useGetFertWorkSessionsQuery,
  useGetRunsQuery,
  useGetUnassignedDonorsQuery,
  useMoveDonorBetweenRunsMutation,
  useRegenerateRunsMutation,
  useSetRunNameMutation,
} from '@/app/services/fertWork';
import { PageStatusBanner } from '@/components/common';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useMoveSireGroupBetweenRunsMutation } from '@/services/fert-work';
import { useFertWorkRunSheetReport } from '@/services/reports';
import { useToast } from '@/services/toast';

import { formatTime, parseIsoDate } from '../../utils/dateHelpers';
import {
  buildFertWindowWarningMessage,
  formatFertWindow,
  getFertWindowMinutes,
  isFertWindowTooShort,
  isReverseSortRun,
} from '../../utils/fertWindow';
import { useRegenerateConfirm } from '../RegenerateConfirm';
import { DraggableDonor } from './DraggableDonor';
import { DraggableSireGroup } from './DraggableSireGroup';
import { DroppableRun } from './DroppableRun';

function getSemenTypeClass(semenType?: string): string {
  const st = semenType?.trim();
  if (st === 'SF F' || st === 'RS F') return 'text-pink-600';
  if (st === 'SF M' || st === 'RS M') return 'text-blue-600';
  return 'text-gray-700';
}

function useSireSectionId() {
  const donorGroupRomanNumerals = useFeatureFlag(
    'Fert_Work_Donor_Group_Roman_Numerals',
  );
  return useCallback(
    (runId: string, sire: SireInfo): string =>
      donorGroupRomanNumerals
        ? `${runId}-${sire.id}-${sire.semenType || ''}-${sire.collectionCode || ''}-${sire.note || ''}`
        : `${runId}-${sire.id}`,
    [donorGroupRomanNumerals],
  );
}

function RegenerateDropdown({
  onSelect,
  isLoading,
}: {
  onSelect: (hours: number) => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const savedHours = parseInt(
    localStorage.getItem('fertwork-mat-time-window') || '3',
  );

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (!isLoading) setOpen(true);
      }}
      onMouseLeave={() => {
        timeoutRef.current = setTimeout(() => setOpen(false), 200);
      }}
    >
      <Button
        variant="warning"
        onClick={() => onSelect(savedHours)}
        disabled={isLoading}
        className="text-sm"
      >
        <i
          className={isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'}
          data-icon="inline-start"
        />
        {t('fertWork.regenerate', 'Regenerate')}
      </Button>
      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 w-44 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          <button
            type="button"
            className="w-full border-0 bg-transparent px-4 py-2 text-left text-sm text-gray-700 hover:bg-cyan-50 hover:text-cyan-700"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onSelect(3);
            }}
          >
            {t('fertWork.hourWindow', '{hours} Hour Window', { hours: 3 })}
          </button>
          <button
            type="button"
            className="w-full border-0 bg-transparent px-4 py-2 text-left text-sm text-gray-700 hover:bg-cyan-50 hover:text-cyan-700"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onSelect(2);
            }}
          >
            {t('fertWork.hourWindow', '{hours} Hour Window', { hours: 2 })}
          </button>
        </div>
      )}
    </div>
  );
}

export type GenerateRunsProps = {
  sessionId: string;
  className?: string;
  onRunsChanged?: () => void;
};

export const GenerateRuns = ({
  sessionId,
  className = '',
  onRunsChanged,
}: GenerateRunsProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const showRegenerateConfirm = useRegenerateConfirm();

  const [activeDonor, setActiveDonor] = useState<{
    donor: DonorInfo;
    sourceRunId: string | null;
  } | null>(null);
  const [activeSireGroup, setActiveSireGroup] = useState<{
    sire: SireInfo;
    sourceRunId: string;
  } | null>(null);
  const [overRunId, setOverRunId] = useState<string | null>(null);
  const runsBaselineRef = useRef<string | null>(null);
  const runsSnapshotRef = useRef<unknown>(null);
  const [isStaleData, setIsStaleData] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [expandedInitShape, setExpandedInitShape] = useState<string | null>(
    null,
  );
  const [showFab, setShowFab] = useState(false);
  const actionsRowRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
  );

  const [generateRunSheetPdf] = useFertWorkRunSheetReport();
  const [generateRuns, { isLoading: isGenerating }] = useGenerateRunsMutation();
  const [regenerateRuns, { isLoading: isRegenerating }] =
    useRegenerateRunsMutation();
  const [moveDonor, { isLoading: isMoving }] =
    useMoveDonorBetweenRunsMutation();
  const { mutateAsync: moveSireGroup, isPending: isMovingSireGroup } =
    useMoveSireGroupBetweenRunsMutation();
  const donorGroupRomanNumerals = useFeatureFlag(
    'Fert_Work_Donor_Group_Roman_Numerals',
  );
  const sectionIdShape = donorGroupRomanNumerals ? 'withNote' : 'legacy';
  const getSireSectionId = useSireSectionId();
  const showFertWindow = useFeatureFlag('Change_Mat_Time_Window');
  const [deleteRun, { isLoading: isDeleting }] = useDeleteRunMutation();
  const [setRunName] = useSetRunNameMutation();
  const [editingRunId, setEditingRunId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const beginEditRunName = useCallback((run: Run) => {
    setEditingRunId(run.id);
    setEditingName(run.customName ?? '');
  }, []);

  const cancelEditRunName = useCallback(() => {
    setEditingRunId(null);
    setEditingName('');
  }, []);

  const commitEditRunName = useCallback(
    async (run: Run) => {
      const next = editingName.trim();
      const prev = (run.customName ?? '').trim();
      if (next === prev) {
        cancelEditRunName();
        return;
      }
      try {
        await setRunName({
          sessionId,
          runId: run.id,
          customName: next.length === 0 ? null : next,
        }).unwrap();
      } catch (err) {
        console.error('Failed to save run name', err);
      }
      cancelEditRunName();
    },
    [editingName, sessionId, setRunName, cancelEditRunName],
  );
  const [createEmptyRun, { isLoading: isCreatingRun }] =
    useCreateEmptyRunMutation();
  const [assignDonor, { isLoading: isAssigning }] =
    useAssignDonorToRunMutation();
  const {
    data: runs = [],
    isLoading,
    isFetching,
    error: loadError,
    refetch,
  } = useGetRunsQuery(sessionId, {
    skip: !sessionId,
  });
  const { data: unassignedDonors = [], refetch: refetchUnassigned } =
    useGetUnassignedDonorsQuery(sessionId, {
      skip: !sessionId,
    });

  const toggleSectionExpanded = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const getAllSectionIds = useCallback(
    (runData: Run[]) => {
      const ids: string[] = [];
      runData.forEach((run) => {
        (run.sires || []).forEach((sire) => {
          ids.push(getSireSectionId(run.id, sire));
        });
      });
      return ids;
    },
    [getSireSectionId],
  );

  const getSectionIdsForRun = useCallback(
    (run: Run) => {
      return (run.sires || []).map((sire) => getSireSectionId(run.id, sire));
    },
    [getSireSectionId],
  );

  const expandAllForRun = useCallback(
    (run: Run) => {
      const runSectionIds = getSectionIdsForRun(run);
      setExpandedSections((prev) => {
        const next = new Set(prev);
        runSectionIds.forEach((id) => next.add(id));
        return next;
      });
    },
    [getSectionIdsForRun],
  );

  const collapseAllForRun = useCallback(
    (run: Run) => {
      const runSectionIds = getSectionIdsForRun(run);
      setExpandedSections((prev) => {
        const next = new Set(prev);
        runSectionIds.forEach((id) => next.delete(id));
        return next;
      });
    },
    [getSectionIdsForRun],
  );

  const isAllExpandedForRun = useCallback(
    (run: Run) => {
      const runSectionIds = getSectionIdsForRun(run);
      return (
        runSectionIds.length > 0 &&
        runSectionIds.every((id) => expandedSections.has(id))
      );
    },
    [expandedSections, getSectionIdsForRun],
  );

  const { data: sessions = [], refetch: refetchSession } =
    useGetFertWorkSessionsQuery(
      { sessionId },
      { skip: !sessionId, pollingInterval: 30000 },
    );
  const currentSession = sessions.find((s) => s.sessionId === sessionId);

  useEffect(() => {
    if (runs.length === 0 || isFetching || !currentSession?.updatedAt) return;

    if (runsSnapshotRef.current !== runs) {
      runsSnapshotRef.current = runs;
      runsBaselineRef.current = currentSession.updatedAt;
      setIsStaleData(false);
      return;
    }

    if (
      runsBaselineRef.current &&
      runsSnapshotRef.current &&
      new Date(currentSession.updatedAt).getTime() >
        new Date(runsBaselineRef.current).getTime()
    ) {
      setIsStaleData(true);
    }
  }, [runs, isFetching, currentSession?.updatedAt]);

  useEffect(() => {
    if (runs.length > 0 && expandedInitShape !== sectionIdShape) {
      setExpandedSections(new Set(getAllSectionIds(runs)));
      setExpandedInitShape(sectionIdShape);
    }
  }, [runs, expandedInitShape, sectionIdShape]);

  useEffect(() => {
    const el = actionsRowRef.current;
    if (!el) {
      setShowFab(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setShowFab(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [runs.length > 0, isLoading, isGenerating, isRegenerating]);

  const handleRefreshAndRevalidate = useCallback(
    (matTimeWindowHours?: number) => {
      if (matTimeWindowHours) {
        localStorage.setItem(
          'fertwork-mat-time-window',
          String(matTimeWindowHours),
        );
      }
      showRegenerateConfirm({
        header: t('fertWork.regenerateRuns', 'Regenerate Runs'),
        description: t(
          'fertWork.regenerateRunsDescription',
          'This will regenerate all runs from the current session data.',
        ),
        warningTitle: t(
          'fertWork.manualChangesWillBeLost',
          'Manual changes will be lost',
        ),
        warningDetail: t(
          'fertWork.customArrangementsReset',
          'Any custom run arrangements (moved donors, added runs) will be reset.',
        ),
        onAccept: async () => {
          try {
            const hours =
              matTimeWindowHours ??
              parseInt(localStorage.getItem('fertwork-mat-time-window') || '3');
            const updatedRuns = await regenerateRuns({
              sessionId,
              matTimeWindowHours: hours,
            }).unwrap();
            setIsStaleData(false);
            setExpandedInitShape(null);
            const updated = await refetchSession();
            const freshSession = updated.data?.find(
              (s) => s.sessionId === sessionId,
            );
            if (freshSession?.updatedAt) {
              runsBaselineRef.current = freshSession.updatedAt;
            }
            onRunsChanged?.();
            toast.show({
              severity: 'success',
              summary: t('fertWork.toasts.runsRefreshed', 'Runs Refreshed'),
              detail: t(
                'fertWork.toasts.runsRefreshedDetail',
                'Successfully regenerated {count} runs with latest data',
                {
                  count: updatedRuns.length,
                },
              ),
              life: 3000,
            });
          } catch (error) {
            console.error('Failed to refresh runs:', error);
            toast.show({
              severity: 'error',
              summary: t('fertWork.toasts.runRefreshFailed', 'Refresh Failed'),
              detail: t(
                'fertWork.toasts.runRefreshFailedDetail',
                'Failed to refresh runs. Please try again.',
              ),
              life: 5000,
            });
          }
        },
      });
    },
    [sessionId, regenerateRuns, refetchSession, onRunsChanged],
  );

  const handleCreateEmptyRun = useCallback(async () => {
    try {
      await createEmptyRun({
        sessionId,
        afterRunId: selectedRunId ?? undefined,
      }).unwrap();
      toast.show({
        severity: 'success',
        summary: t('fertWork.toasts.runCreated', 'Run Created'),
        detail: t(
          'fertWork.toasts.runCreatedDetail',
          'Empty run created. Drag donors to populate it.',
        ),
        life: 3000,
      });
      refetch();
      onRunsChanged?.();
    } catch (error) {
      console.error('Failed to create run:', error);
      toast.show({
        severity: 'error',
        summary: t('fertWork.toasts.createRunFailed', 'Create Failed'),
        detail: t(
          'fertWork.toasts.createRunFailedDetail',
          'Failed to create empty run. Please try again.',
        ),
        life: 5000,
      });
    }
  }, [sessionId, createEmptyRun, refetch, selectedRunId]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const data = active.data.current as
      | { donor: DonorInfo; sourceRunId: string | null }
      | { sire: SireInfo; sourceRunId: string; type: 'sireGroup' }
      | undefined;
    if (!data) return;
    if ('type' in data && data.type === 'sireGroup') {
      setActiveSireGroup({ sire: data.sire, sourceRunId: data.sourceRunId });
    } else if ('donor' in data) {
      setActiveDonor(data);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (over) {
      setOverRunId(over.id as string);
    } else {
      setOverRunId(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { over } = event;
    const currentActiveDonor = activeDonor;
    const currentActiveSireGroup = activeSireGroup;
    setActiveDonor(null);
    setActiveSireGroup(null);
    setOverRunId(null);

    if (!over) return;
    const targetRunId = over.id as string;

    if (currentActiveSireGroup) {
      if (currentActiveSireGroup.sourceRunId === targetRunId) return;
      try {
        await moveSireGroup({
          sessionId,
          sireId: currentActiveSireGroup.sire.id,
          semenType: currentActiveSireGroup.sire.semenType,
          collectionCode: currentActiveSireGroup.sire.collectionCode,
          sireNote: currentActiveSireGroup.sire.note,
          sourceRunId: currentActiveSireGroup.sourceRunId,
          targetRunId,
        });
        toast.show({
          severity: 'success',
          summary: t('fertWork.toasts.sireGroupMoved', 'Group Moved'),
          detail: t(
            'fertWork.toasts.sireGroupMovedDetail',
            '{name} group moved successfully',
            {
              name: currentActiveSireGroup.sire.name,
            },
          ),
          life: 3000,
        });
        refetch();
        onRunsChanged?.();
      } catch (error) {
        console.error('Failed to move sire group:', error);
        toast.show({
          severity: 'error',
          summary: t('fertWork.toasts.operationFailed', 'Operation Failed'),
          detail: t(
            'fertWork.toasts.operationFailedDetail',
            'Failed to move/assign donor. Please try again.',
          ),
          life: 5000,
        });
      }
      return;
    }

    if (!currentActiveDonor) return;
    if (currentActiveDonor.sourceRunId === targetRunId) return;

    try {
      if (currentActiveDonor.sourceRunId === null) {
        await assignDonor({
          sessionId,
          runId: targetRunId,
          donorDetailId: currentActiveDonor.donor.id,
        }).unwrap();

        toast.show({
          severity: 'success',
          summary: t('fertWork.toasts.donorAssigned', 'Donor Assigned'),
          detail: t(
            'fertWork.toasts.donorAssignedDetail',
            '{name} assigned to run',
            {
              name: currentActiveDonor.donor.name,
            },
          ),
          life: 3000,
        });

        refetch();
        refetchUnassigned();
        onRunsChanged?.();
      } else {
        await moveDonor({
          sessionId,
          donorDetailId: currentActiveDonor.donor.id,
          sourceRunId: currentActiveDonor.sourceRunId,
          targetRunId,
        }).unwrap();

        toast.show({
          severity: 'success',
          summary: t('fertWork.toasts.donorMoved', 'Donor Moved'),
          detail: t(
            'fertWork.toasts.donorMovedDetail',
            '{name} moved successfully',
            {
              name: currentActiveDonor.donor.name,
            },
          ),
          life: 3000,
        });

        refetch();
        onRunsChanged?.();
      }
    } catch (error) {
      console.error('Failed to move/assign donor:', error);
      toast.show({
        severity: 'error',
        summary: t('fertWork.toasts.operationFailed', 'Operation Failed'),
        detail: t(
          'fertWork.toasts.operationFailedDetail',
          'Failed to move/assign donor. Please try again.',
        ),
        life: 5000,
      });
    }
  };

  const handleDragCancel = () => {
    setActiveDonor(null);
    setActiveSireGroup(null);
    setOverRunId(null);
  };

  const handleDeleteRun = (runId: string, runName: string) => {
    confirm({
      description: t(
        'fertWork.deleteRunConfirm',
        'Are you sure you want to delete {runName}? The donors will be unassigned from this run.',
        { runName },
      ),
      title: t('fertWork.deleteRun', 'Delete Run'),
      confirmVariant: 'destructive',
      onConfirm: async () => {
        try {
          await deleteRun({ sessionId, runId }).unwrap();
          toast.show({
            severity: 'success',
            summary: t('fertWork.toasts.runDeleted', 'Run Deleted'),
            detail: t(
              'fertWork.toasts.runDeletedDetail',
              '{name} has been deleted',
              { name: runName },
            ),
            life: 3000,
          });
          refetch();
          onRunsChanged?.();
        } catch (error) {
          console.error('Failed to delete run:', error);
          toast.show({
            severity: 'error',
            summary: t('fertWork.toasts.deleteRunFailed', 'Delete Failed'),
            detail: t(
              'fertWork.toasts.deleteRunFailedDetail',
              'Failed to delete run. Please try again.',
            ),
            life: 5000,
          });
        }
      },
    });
  };

  const getDisplayWarningMessage = (run: Run) => {
    if (!showFertWindow) return run.warningMessage;
    return buildFertWindowWarningMessage(
      run.warningMessage,
      getFertWindowMinutes(run.runStartTime, run.runEndTime),
      t(
        'fertWork.fertWindowLessThanHour',
        'Fert window is less than 1 hour. Ideal window is 1 hour or more.',
      ),
      isReverseSortRun(run.semenTypes),
    );
  };

  const renderValidationBadge = (run: Run) => {
    if (run.validationLevel === 'invalid') {
      return (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1 text-sm font-medium text-red-600">
            <i className="pi pi-times-circle" />
            <span>{t('fertWork.invalid', 'Invalid')}</span>
          </div>
          {run.validationMessage && (
            <div className="text-xs text-red-600">{run.validationMessage}</div>
          )}
          {run.suggestedAction && (
            <div className="text-xs text-red-500 italic">
              {run.suggestedAction}
            </div>
          )}
        </div>
      );
    }

    const warningMessage = getDisplayWarningMessage(run);

    if (
      run.validationLevel === 'warning' ||
      (showFertWindow && warningMessage)
    ) {
      return (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1 text-sm font-medium text-green-600">
            <i className="pi pi-check-circle" />
            <span>{t('fertWork.valid', 'Valid')}</span>
          </div>
          {warningMessage && (
            <div className="flex items-center gap-1 text-xs text-yellow-600">
              <i className="pi pi-exclamation-triangle" />
              <span>{warningMessage}</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1 text-sm font-medium text-green-600">
        <i className="pi pi-check-circle" />
        <span>{t('fertWork.valid', 'Valid')}</span>
      </div>
    );
  };

  const renderRun = (run: Run, runIndex = 0) => {
    const isDragSource =
      activeDonor?.sourceRunId === run.id ||
      activeSireGroup?.sourceRunId === run.id;
    const isInvalid = run.validationLevel === 'invalid';
    const fertWindowMinutes = getFertWindowMinutes(
      run.runStartTime,
      run.runEndTime,
    );
    const isFirstRun = runIndex === 0;
    const isSelected = selectedRunId === run.id;

    return (
      <DroppableRun
        key={run.id}
        runId={run.id}
        isInvalid={isInvalid}
        isDragSource={isDragSource}
        dataTour={runIndex === 1 ? 'runs-drop-target' : undefined}
        isSelected={isSelected}
        onClick={() => setSelectedRunId(isSelected ? null : run.id)}
        speciesSubfamily={run.speciesSubfamily}
      >
        <div className="mb-3 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              {editingRunId === run.id ? (
                <span
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') commitEditRunName(run);
                      else if (e.key === 'Escape') cancelEditRunName();
                    }}
                    onBlur={() => commitEditRunName(run)}
                    autoFocus
                    maxLength={100}
                    placeholder={run.runId}
                    className="h-8 text-sm"
                  />
                </span>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    beginEditRunName(run);
                  }}
                  title={t('fertWork.editRunName', 'Edit run name')}
                  className="group/name inline-flex h-8 min-w-[96px] cursor-pointer items-center justify-center gap-1.5 rounded-md border-2 border-gray-300 bg-white px-3 transition-colors hover:border-gray-400 hover:bg-gray-50 focus:ring-2 focus:ring-blue-100 focus:outline-none"
                >
                  <span
                    className={`text-sm ${run.customName ? 'font-semibold' : 'font-medium'} text-gray-900`}
                  >
                    {run.customName || run.runId}
                  </span>
                  <i className="pi pi-pencil text-[11px] text-gray-400 opacity-0 transition-opacity group-hover/name:opacity-100" />
                </button>
              )}
              {run.speciesSubfamily === 'Ovine' && (
                <span className="rounded border border-orange-300 bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-orange-800">
                  OV
                </span>
              )}
              {run.speciesSubfamily === 'Caprine' && (
                <span className="rounded border border-green-300 bg-green-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-green-800">
                  CAP
                </span>
              )}
            </div>
            <div className="mt-2">{renderValidationBadge(run)}</div>
            {run.hasTimeWarning && run.validationLevel !== 'invalid' && (
              <div className="mt-1 flex items-center gap-1 text-xs text-yellow-600">
                <i className="pi pi-clock" />
                <span>
                  {t(
                    'fertWork.runTimeLessThanHour',
                    'Run time < 1 hour - start prep early',
                  )}
                </span>
              </div>
            )}
          </div>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-destructive"
                  onClick={() => handleDeleteRun(run.id, run.runId)}
                >
                  <i className="pi pi-trash" />
                  <span className="sr-only">
                    {t('fertWork.deleteRun', 'Delete Run')}
                  </span>
                </Button>
              }
            />
            <TooltipContent side="left">
              {t('fertWork.deleteRun', 'Delete Run')}
            </TooltipContent>
          </Tooltip>
        </div>

        <div className="mb-4 grid grid-cols-3 gap-4 rounded-md bg-gray-50 p-3 md:grid-cols-6">
          <div>
            <div className="text-xs text-gray-500">
              {t('fertWork.totalOocytesLabel', 'TOTAL OOCYTES')}
            </div>
            <div className="text-xl font-bold">{run.totalOocytes}</div>
          </div>
          {showFertWindow ? (
            <div>
              <div className="text-xs text-gray-500">
                {t('fertWork.fertWindowLabel', 'FERT WINDOW')}
              </div>
              <div className="text-xl font-bold">
                {formatFertWindow(fertWindowMinutes)}
              </div>
              {isFertWindowTooShort(fertWindowMinutes) && (
                <div className="text-xs text-yellow-600">
                  ({fertWindowMinutes} min)
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="text-xs text-gray-500">
                {t('fertWork.matTimeWindowLabel', 'MAT TIME WINDOW')}
              </div>
              <div className="text-xl font-bold">{run.matTimeWindow}</div>
              {run.matTimeWindowMinutes !== undefined &&
                run.matTimeWindowMinutes < 60 && (
                  <div className="text-xs text-yellow-600">
                    ({run.matTimeWindowMinutes} min)
                  </div>
                )}
            </div>
          )}
          <div>
            <div className="text-xs text-gray-500">
              {t('fertWork.startTime', 'START TIME')}
            </div>
            <div className="text-sm font-medium">
              {formatTime(parseIsoDate(run.runStartTime))}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">
              {t('fertWork.endTime', 'END TIME')}
            </div>
            <div className="text-sm font-medium">
              {formatTime(parseIsoDate(run.runEndTime))}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">
              {t('fertWork.siresLabel', 'SIRES')}
            </div>
            <div className="text-xl font-bold">
              {run.sireCount || run.sires?.length || 0}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">
              {t('fertWork.donorsLabel', 'DONORS')}
            </div>
            <div className="text-xl font-bold">{run.donorCount}</div>
          </div>
        </div>

        <div className="mb-4 rounded-md bg-gray-50 p-3">
          <div className="text-xs text-gray-500">
            {t('fertWork.semenTypesLabel', 'SEMEN TYPES')}
          </div>
          <div className="text-sm font-medium">
            {run.semenTypesDisplay || run.semenType || '-'}
          </div>
        </div>

        <div className="mb-3">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {t('fertWork.siresAndDonors', 'SIRES & DONORS:')}
            </span>
            {(run.sires || []).length > 0 && (
              <button
                type="button"
                className="flex items-center gap-1 rounded-md border border-gray-200 bg-transparent px-2 py-1.5 text-xs leading-none text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900"
                onClick={() =>
                  isAllExpandedForRun(run)
                    ? collapseAllForRun(run)
                    : expandAllForRun(run)
                }
              >
                <i
                  className={`pi ${isAllExpandedForRun(run) ? 'pi-chevron-up' : 'pi-chevron-down'} text-[10px]`}
                />
                {isAllExpandedForRun(run)
                  ? t('fertWork.collapseAll', 'Collapse all')
                  : t('fertWork.expandAll', 'Expand all')}
              </button>
            )}
          </div>
          <div className="flex flex-col gap-1">
            {(run.sires || []).map((sire, sireIndex) => {
              const sireDonors = sire.donors || [];
              const sectionId = getSireSectionId(run.id, sire);
              const isExpanded = expandedSections.has(sectionId);
              const sireHeader = (
                <div
                  className="flex cursor-pointer items-center gap-2 text-sm"
                  onClick={() => toggleSectionExpanded(sectionId)}
                >
                  <i
                    className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'} text-xs text-gray-400`}
                  />
                  <span
                    className={`font-medium ${getSemenTypeClass(sire.semenType)}`}
                  >
                    {sire.name}
                    {sire.note && ` ${sire.note}`}
                  </span>
                  {sire.semenType && (
                    <span
                      className={`text-xs ${getSemenTypeClass(sire.semenType)}`}
                    >
                      [{sire.semenType}]
                    </span>
                  )}
                  {sire.pelletNumber && (
                    <span
                      className={`text-xs ${getSemenTypeClass(sire.semenType)}`}
                    >
                      • {sire.pelletNumber}
                    </span>
                  )}
                  <span className="text-xs text-gray-500">
                    {t('fertWork.donorsCount', '({count} donors)', {
                      count: sireDonors.length,
                    })}
                  </span>
                </div>
              );
              return (
                <div
                  key={`${sire.id}-${sireIndex}`}
                  className="border-b border-gray-100 py-1 last:border-b-0"
                >
                  {runs.length > 1 ? (
                    <DraggableSireGroup sire={sire} sourceRunId={run.id}>
                      {sireHeader}
                    </DraggableSireGroup>
                  ) : (
                    sireHeader
                  )}
                  {isExpanded && (
                    <div className="mt-2 flex flex-wrap gap-1.5 pl-5">
                      {sireDonors.map((donor, donorIndex) => (
                        <DraggableDonor
                          key={`${donor.id}-${donorIndex}`}
                          donor={donor}
                          sourceRunId={run.id}
                          dataTour={
                            isFirstRun && sireIndex === 0 && donorIndex === 0
                              ? 'runs-donor-handle'
                              : undefined
                          }
                        >
                          <div className="cursor-grab rounded border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs leading-none hover:border-blue-300 hover:bg-blue-50">
                            {donor.name || donor.id} ({donor.breed}) -{' '}
                            {donor.oocytes}
                            {donor.matTime && (
                              <span className="ml-1 text-gray-500">
                                @ {formatTime(new Date(donor.matTime))}
                              </span>
                            )}
                          </div>
                        </DraggableDonor>
                      ))}
                      {sireDonors.length === 0 && (
                        <span className="text-xs text-gray-400">
                          {t('fertWork.noDonors', 'No donors')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {(!run.sires || run.sires.length === 0) && (
              <div className="text-sm text-gray-500">
                {t('fertWork.noSires', 'No sires')}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          {(() => {
            const printDisabledReason = isStaleData
              ? t(
                  'fertWork.cannotPrintStale',
                  'Cannot print yet: session was updated elsewhere. Use Refresh & Revalidate (your run layout is preserved).',
                )
              : run.validationLevel === 'invalid'
                ? t(
                    'fertWork.cannotPrintInvalid',
                    'Cannot print: Run is invalid',
                  )
                : undefined;
            const printButton = (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  generateRunSheetPdf({ sessionId, runId: run.id })
                }
                disabled={!!printDisabledReason}
              >
                <i className="pi pi-print" data-icon="inline-start" />
                {t('fertWork.printRunSheet', 'Print Run Sheet')}
              </Button>
            );
            return printDisabledReason ? (
              <Tooltip>
                <TooltipTrigger
                  render={<span className="inline-block">{printButton}</span>}
                />
                <TooltipContent side="top">
                  {printDisabledReason}
                </TooltipContent>
              </Tooltip>
            ) : (
              printButton
            );
          })()}
        </div>
      </DroppableRun>
    );
  };

  return (
    <div
      className={`generate-runs-page relative flex flex-auto flex-col ${className}`}
    >
      {(isMoving || isDeleting || isAssigning) && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50">
          <div className="flex items-center gap-2 rounded-lg bg-white p-4 shadow-lg">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            <span>
              {isDeleting
                ? t('fertWork.deletingRun', 'Deleting run...')
                : isAssigning
                  ? t('fertWork.assigningDonor', 'Assigning donor...')
                  : t('fertWork.movingDonor', 'Moving donor...')}
            </span>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-4">
        {(isLoading || isGenerating || isRegenerating) && (
          <PageStatusBanner
            type="loading"
            message={
              isGenerating
                ? t('fertWork.generatingRuns', 'Generating runs...')
                : isRegenerating
                  ? t('fertWork.generatingRuns', 'Generating runs...')
                  : t('fertWork.loadingRuns', 'Loading runs...')
            }
          />
        )}

        {loadError && !isLoading && (
          <PageStatusBanner
            type="error"
            message={t(
              'fertWork.failedToLoadRuns',
              'Failed to load runs. Please try again.',
            )}
          />
        )}

        {!isLoading && !isGenerating && !isRegenerating && runs.length > 0 && (
          <>
            {(() => {
              const hasInvalidRuns = runs.some(
                (run) => run.validationLevel === 'invalid',
              );
              const invalidCount = runs.filter(
                (run) => run.validationLevel === 'invalid',
              ).length;
              const canPrint = !hasInvalidRuns && !isStaleData;

              const getTooltipMessage = () => {
                if (isStaleData)
                  return t(
                    'fertWork.cannotPrintStale',
                    'Cannot print yet: session was updated elsewhere. Use Refresh & Revalidate (your run layout is preserved).',
                  );
                if (hasInvalidRuns)
                  return t(
                    'fertWork.cannotPrintInvalidRuns',
                    'Cannot print: {count} invalid run(s) must be fixed first',
                    {
                      count: invalidCount,
                    },
                  );
                return undefined;
              };

              return (
                <div
                  data-tour="runs-actions"
                  ref={actionsRowRef}
                  className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                >
                  <div className="flex flex-wrap gap-2">
                    <RegenerateDropdown
                      onSelect={handleRefreshAndRevalidate}
                      isLoading={isRegenerating}
                    />
                    {(() => {
                      const tooltipMessage = getTooltipMessage();
                      const printSummaryButton = (
                        <Button
                          onClick={() => generateRunSheetPdf({ sessionId })}
                          disabled={!canPrint}
                          className="text-sm"
                        >
                          <i className="pi pi-print" data-icon="inline-start" />
                          {t('fertWork.printSummary', 'Print Summary')}
                        </Button>
                      );
                      return tooltipMessage ? (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <span className="inline-block">
                                {printSummaryButton}
                              </span>
                            }
                          />
                          <TooltipContent side="top">
                            {tooltipMessage}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        printSummaryButton
                      );
                    })()}
                    <Button
                      variant="outline"
                      onClick={handleCreateEmptyRun}
                      disabled={isCreatingRun}
                      className="text-sm"
                    >
                      <i
                        className={
                          isCreatingRun ? 'pi pi-spin pi-spinner' : 'pi pi-plus'
                        }
                        data-icon="inline-start"
                      />
                      {t('fertWork.addRun', 'Add Run')}
                    </Button>
                  </div>
                  {isStaleData ? (
                    <span className="text-xs text-yellow-700 sm:text-sm">
                      <i className="pi pi-exclamation-triangle mr-1" />
                      <span className="hidden sm:inline">
                        {t(
                          'fertWork.staleDataWarning',
                          'Session updated elsewhere — use Refresh & Revalidate (not browser reload) to sync; your work here is preserved',
                        )}
                      </span>
                      <span className="sm:hidden">
                        {t(
                          'fertWork.staleData',
                          'Updated elsewhere — use Refresh & Revalidate',
                        )}
                      </span>
                    </span>
                  ) : hasInvalidRuns ? (
                    <span className="text-xs text-red-600 sm:text-sm">
                      <i className="pi pi-exclamation-triangle mr-1" />
                      {invalidCount} {t('fertWork.invalid', 'Invalid')}
                    </span>
                  ) : (
                    <div
                      data-tour="runs-drag-hint"
                      className="hidden items-center gap-2 text-sm text-gray-500 sm:flex"
                    >
                      <i className="pi pi-info-circle" />
                      <span>
                        {t(
                          'fertWork.dragDonorsHint',
                          'Drag donors between runs to reorganize',
                        )}
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}

            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              {unassignedDonors.length > 0 && (
                <div
                  data-tour="runs-unassigned"
                  className="rounded-lg border-2 border-dashed border-orange-300 bg-orange-50 p-4"
                >
                  <div className="mb-3 flex items-center gap-2">
                    <i className="pi pi-exclamation-circle text-orange-600" />
                    <span className="font-medium text-orange-800">
                      {t(
                        'fertWork.unassignedDonors',
                        'Unassigned Donors ({count})',
                        {
                          count: unassignedDonors.length,
                        },
                      )}
                    </span>
                    <span className="text-sm text-orange-600">
                      {t(
                        'fertWork.dragToAssign',
                        '- Drag to a run below to assign',
                      )}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {unassignedDonors.map((donor) => (
                      <DraggableDonor
                        key={donor.id}
                        donor={donor}
                        sourceRunId={null}
                      >
                        <div className="cursor-grab rounded border border-orange-300 bg-white px-3 py-2 text-sm shadow-sm transition-all hover:border-orange-400 hover:shadow">
                          <div className="font-medium text-gray-900">
                            {donor.name}
                          </div>
                          <div className="text-xs text-gray-600">
                            {donor.breed} • {donor.oocytes} oocytes
                            {donor.sireName && ` • ${donor.sireName}`}
                            {donor.semenType && ` • ${donor.semenType}`}
                          </div>
                          {donor.matTime && (
                            <div className="text-xs text-gray-500">
                              Mat: {formatTime(new Date(donor.matTime))}
                            </div>
                          )}
                        </div>
                      </DraggableDonor>
                    ))}
                  </div>
                </div>
              )}

              <div
                data-tour="runs-grid"
                className="grid grid-cols-1 gap-4 md:grid-cols-2"
              >
                {runs.map((run, runIndex) => renderRun(run, runIndex))}
              </div>

              <DragOverlay>
                {activeDonor ? (
                  <div className="cursor-grabbing rounded border border-blue-500 bg-blue-100 px-3 py-2 text-sm shadow-lg">
                    <div className="font-medium text-gray-900">
                      {activeDonor.donor.name}
                    </div>
                    <div className="text-xs text-gray-600">
                      {activeDonor.donor.breed} • {activeDonor.donor.oocytes}{' '}
                      oocytes
                    </div>
                  </div>
                ) : activeSireGroup ? (
                  <div className="cursor-grabbing rounded border border-purple-500 bg-purple-100 px-3 py-2 text-sm shadow-lg">
                    <div className="font-medium text-gray-900">
                      {activeSireGroup.sire.name}
                      {activeSireGroup.sire.semenType &&
                        ` [${activeSireGroup.sire.semenType}]`}
                    </div>
                    <div className="text-xs text-gray-600">
                      {activeSireGroup.sire.donorCount} donors •{' '}
                      {t('fertWork.moveGroup', 'Move Group')}
                    </div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </>
        )}

        {!isLoading &&
          !isGenerating &&
          !isRegenerating &&
          runs.length === 0 && (
            <PageStatusBanner
              type="empty"
              message={t(
                'fertWork.noRunsAvailable',
                'No runs available for this session',
              )}
            />
          )}

        {showFab && runs.length > 0 && (
          <div className="group fixed right-4 bottom-4 z-50 flex flex-col-reverse items-center gap-2 sm:right-6 sm:bottom-6 sm:gap-3">
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    size="icon"
                    className="!h-12 !w-12 rounded-full shadow-lg sm:!h-14 sm:!w-14"
                    onClick={handleCreateEmptyRun}
                    disabled={isCreatingRun}
                  >
                    <i
                      className={
                        isCreatingRun ? 'pi pi-spin pi-spinner' : 'pi pi-plus'
                      }
                    />
                    <span className="sr-only">
                      {t('fertWork.addRun', 'Add Run')}
                    </span>
                  </Button>
                }
              />
              <TooltipContent side="left">
                {t('fertWork.addRun', 'Add Run')}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="warning"
                    size="icon"
                    className="!h-9 !w-9 translate-y-4 rounded-full opacity-0 shadow-lg transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100 sm:!h-10 sm:!w-10"
                    onClick={() => handleRefreshAndRevalidate()}
                    disabled={isRegenerating}
                  >
                    <i
                      className={
                        isRegenerating
                          ? 'pi pi-spin pi-spinner'
                          : 'pi pi-refresh'
                      }
                    />
                    <span className="sr-only">
                      {t('fertWork.regenerate', 'Regenerate')}
                    </span>
                  </Button>
                }
              />
              <TooltipContent side="left">
                {t('fertWork.regenerate', 'Regenerate')}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="info"
                    size="icon"
                    className="!h-9 !w-9 translate-y-8 rounded-full opacity-0 shadow-lg transition-all delay-75 duration-200 group-hover:translate-y-0 group-hover:opacity-100 sm:!h-10 sm:!w-10"
                    onClick={() => generateRunSheetPdf({ sessionId })}
                  >
                    <i className="pi pi-print" />
                    <span className="sr-only">
                      {t('fertWork.printSummary', 'Print Summary')}
                    </span>
                  </Button>
                }
              />
              <TooltipContent side="left">
                {t('fertWork.printSummary', 'Print Summary')}
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};
